import React, { useCallback, useContext, useMemo } from "react";
import type { ChangeEvent, ReactNode } from "react";
import { Checkbox, Typography, Divider } from "@mui/material";

import { subscriptionHelper } from "@/features/subscriptions/helpers/subscriptionHelper";
import StatusChip from "@/components/muiCustom/chip/StatusChip";
import { getFormatDate } from "@/helpers/utils";
import { MUICustomAutocompleteSelect } from "@/components/muiCustom/select/MUICustomAutocompleteSelect";
import {
  LOCATION_STATUS_CONST,
  stripeSubscriptionStatus,
  SUBSCRIPTION_CONST,
} from "@/config/constants";
import useConfirmDialog from "@/helpers/hooks/useConfirmDialog";
import { GlobalContext } from "@/helpers/contexts/contexts";
import { TemporaryClosedStatusNote } from "@/components/muiCustom/note/InlineStatusNote";

type SubscriptionPackage = {
  currency?: string;
  id?: string | number;
  payment_type?: string;
  period?: string | number;
  title?: string;
  [key: string]: unknown;
};

type SubscriptionLocation = {
  country?: string;
  duration?: string | number;
  fk_location_id?: string | number;
  fk_participant_id?: string | number;
  is_current_subscription_active?: boolean;
  is_upcoming_subscription?: boolean;
  isDelayed?: boolean;
  location_name?: string;
  location_status?: string | number;
  packageData?: SubscriptionPackage[];
  package_title?: ReactNode;
  participantName?: string;
  subcription_status?: string;
  subscription_end_date?: string;
  subscription_package?: string;
  subscription_start_date?: string;
  taken_upcoming_subscription?: boolean;
  upcoming_end_date?: string;
  upcoming_pacakge_title?: ReactNode;
  upcoming_renewal?: number | boolean;
  upcoming_start_date?: string;
  [key: string]: unknown;
};

type LocPackSelection = {
  locationId?: string | number;
  locationInfo?: SubscriptionLocation;
  packageId?: string | number;
  packageInfo?: SubscriptionPackage;
  [key: string]: unknown;
};

type FormProps = {
  handleBlur: (..._args: unknown[]) => void;
  setFieldValue: (_field: string, _value: LocPackSelection[]) => void;
  values: {
    locPackSelections: LocPackSelection[];
  };
};

type PackageOption = {
  label: string;
  value?: string | number;
};

type LocationProps = {
  formProps: FormProps;
  isLocationBelongsToSameCountry: (
    _locId?: string | number,
    _locPackSelections?: LocPackSelection[]
  ) => boolean;
  isLocationBelongsToSameParticipant: (
    _locId?: string | number,
    _locPackSelections?: LocPackSelection[]
  ) => boolean;
  isPackageBelongsToSameCurrency: (
    _locId?: string | number,
    _packageId?: string | number,
    _locPackSelections?: LocPackSelection[]
  ) => boolean;
  isPackageBelongsToSamePaymentType: (
    _locId?: string | number,
    _packageId?: string | number,
    _locPackSelections?: LocPackSelection[]
  ) => boolean;
  location: SubscriptionLocation;
  packageOptions?: PackageOption[];
  showConfirmDialog: (_title: string, _content: ReactNode) => void;
};

type UserLocationListProps = {
  formProps: FormProps;
  loadMoreLocations: () => ReactNode | void;
  locations?: SubscriptionLocation[];
  totalLocationCount?: number;
};

type PackageOptionsMap = Record<string, PackageOption[]>;

type LocationContainerProps = React.HTMLAttributes<HTMLDivElement> & {
  disabled?: boolean;
};

const LocationContainer =
  "div" as unknown as React.ComponentType<LocationContainerProps>;

const Location = ({
  location,
  formProps,
  packageOptions = [],
  isLocationBelongsToSameParticipant,
  isLocationBelongsToSameCountry,
  isPackageBelongsToSamePaymentType,
  isPackageBelongsToSameCurrency,
  showConfirmDialog,
}: LocationProps) => {
  const { selectedBrand } = useContext(GlobalContext);
  const isChecked = !!formProps.values.locPackSelections.find(
    (l) => String(l.locationId) === String(location.fk_location_id)
  );

  const isLocBelongsToSameParticipant = isLocationBelongsToSameParticipant(
    location.fk_location_id,
    formProps.values.locPackSelections
  );

  const isLocBelongsToSameCountry = isLocationBelongsToSameCountry(
    location.fk_location_id,
    formProps.values.locPackSelections
  );

  const hasUpcomingSubscription =
    // location.subcription_status === stripeSubscriptionStatus.PAST_DUE ||
    location?.taken_upcoming_subscription ||
    (location?.is_upcoming_subscription &&
      !location?.is_current_subscription_active) ||
    location?.upcoming_renewal === 0;

  const renderRenewalLabel = () => {
    const isRenewing = subscriptionHelper.isRenewing(location);
    const isDelayedRenewing = Boolean(location?.isDelayed);
    const isTemporarilyClosed =
      Number(location?.location_status) ===
      LOCATION_STATUS_CONST.TEMPORARILY_CLOSED.ID;

    if (!isRenewing && !isDelayedRenewing && !isTemporarilyClosed) {
      return null;
    }

    return (
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        {isDelayedRenewing && (
          <StatusChip
            label={SUBSCRIPTION_CONST.STATUS.EXPIRED.TEXT}
            status={SUBSCRIPTION_CONST.STATUS.EXPIRED.TYPE}
            className="chip"
          />
        )}

        {isRenewing &&
          location?.upcoming_renewal === 1 &&
          !location?.taken_upcoming_subscription && (
            <span className="mt-1 ml-1">
              <StatusChip
                label={SUBSCRIPTION_CONST.STATUS.UPCOMING_RENEWAL.TEXT}
                status={SUBSCRIPTION_CONST.STATUS.UPCOMING_RENEWAL.TYPE}
                className="chip"
              />
            </span>
          )}

        {isTemporarilyClosed && (
          <TemporaryClosedStatusNote location={location} />
        )}
      </div>
    );
  };

  const pastDueLabel = () => {
    const isPastDue =
      location?.subcription_status === stripeSubscriptionStatus.PAST_DUE;
    if (!isPastDue) return null;

    return (
      <span className="mt-1 ml-1">
        <StatusChip
          label={SUBSCRIPTION_CONST.STATUS.PAST_DUE.TEXT}
          status={SUBSCRIPTION_CONST.STATUS.PAST_DUE.TYPE}
          className="chip"
        />
      </span>
    );
  };

  const handleOnChangePackage = (val?: string | number) => {
    const packageInfo = location.packageData?.find(
      (p) => String(p.id) === String(val)
    );

    if (
      !isPackageBelongsToSamePaymentType(
        location?.fk_location_id,
        val,
        formProps.values.locPackSelections
      )
    ) {
      // alert("Please select same payment type package");
      showConfirmDialog(
        "Wrong Package Selection",
        <Typography>Please select same payment type package.</Typography>
      );
      return;
    }

    if (
      !isPackageBelongsToSameCurrency(
        location?.fk_location_id,
        val,
        formProps.values.locPackSelections
      )
    ) {
      showConfirmDialog(
        "Wrong Package Selection",
        <Typography>
          This package uses a different currency. Please choose a package with
          the same currency as the one already selected.
        </Typography>
      );
      return;
    }

    // New logic
    const isAlreadyAddedLocPack = formProps.values.locPackSelections.find(
      (l) =>
        String(l.locationId) === String(location.fk_location_id) && l?.packageId
    );
    let updatedLocPackSelections = formProps.values.locPackSelections.map(
      (l) =>
        String(l.locationId) === String(location.fk_location_id)
          ? { ...l, packageId: val, packageInfo }
          : l
    );

    if (isAlreadyAddedLocPack) {
      if (!val) {
        //remove package related info from locPackSelections
        updatedLocPackSelections = updatedLocPackSelections.map((l) =>
          String(l.locationId) === String(location.fk_location_id)
            ? {
                locationId: l.locationId,
                locationInfo: l.locationInfo,
              }
            : l
        );
      }
      // update locPackSelections with new values
      formProps.setFieldValue("locPackSelections", updatedLocPackSelections);
    } else if (val) {
      formProps.setFieldValue("locPackSelections", updatedLocPackSelections);
    }
  };

  const handleOnChangeLocation = (e: ChangeEvent<HTMLInputElement>) => {
    const locId = e.target.value;

    const locationInfo = location;

    if (location.subcription_status === stripeSubscriptionStatus.PAST_DUE) {
      showConfirmDialog(
        "Location has past due payment",
        <Typography>
          There are unpaid invoices in your previous subscription, please
          contact your customer success manager
        </Typography>
      );
      return;
    } else if (!isLocBelongsToSameParticipant) {
      // alert(
      //   "You can't select location from different participant."
      // );

      showConfirmDialog(
        "Wrong Participant Selection",
        <Typography>
          You can't select location from different participant.
        </Typography>
      );
      return;
    } else if (!isLocBelongsToSameCountry) {
      showConfirmDialog(
        "Wrong Location Selection",
        <Typography>
          You can’t select locations from different countries , they must be the
          same.
        </Typography>
      );
      return;
    }

    // New logic
    const isAlreadyAdded = formProps.values.locPackSelections.find(
      (l) => String(l.locationId) === String(locId)
    );

    if (isAlreadyAdded) {
      formProps.setFieldValue(
        "locPackSelections",
        formProps.values.locPackSelections.filter(
          (l) => String(l.locationId) !== String(locId)
        )
      );
    } else {
      formProps.setFieldValue("locPackSelections", [
        ...formProps.values.locPackSelections,
        { locationId: locId, locationInfo },
      ]);
    }
  };

  const renderSubscriptionDate = () => {
    if (location?.isDelayed || !location?.subscription_start_date) {
      return null;
    }

    return (
      <>
        <div className="row mb-2 align-items-center">
          <div className="col-md-6 d-flex align-items-center">
            <Typography
              className="subscription-detail mr-1 mt-1 mt-xl-2"
              variant="h6"
            >
              Current Subscription:{" "}
              <Typography
                component="span"
                className="subscription-detail fw-bb d-block"
              >
                {`${getFormatDate(
                  location?.subscription_start_date,
                  "MM/DD/YYYY"
                )} - ${getFormatDate(
                  location?.subscription_end_date,
                  "MM/DD/YYYY"
                )}`}
              </Typography>
            </Typography>
          </div>
          {location?.subscription_package ? (
            <>
              <div className="col-md-1">
                <Divider orientation="vertical" />
              </div>
              <div className="col-md-5">
                <Typography
                  className={`subscription-detail ${
                    hasUpcomingSubscription ? "package " : ""
                  }`}
                  variant="h6"
                >
                  Current Plan:{" "}
                  <Typography
                    component="span"
                    color="text.primary"
                    className={`subscription-detail fw-bb d-block ${
                      hasUpcomingSubscription ? "package" : ""
                    }`}
                  >
                    {location?.package_title} - {location?.duration} Months
                  </Typography>
                </Typography>
              </div>
            </>
          ) : null}
        </div>
        {location?.upcoming_start_date ? (
          <div className="row align-items-center">
            <div className="col-md-6">
              <Typography className="subscription-detail" variant="body2">
                Upcoming Subscription:{" "}
                <Typography
                  component="span"
                  className="subscription-detail fw-bb d-block"
                >
                  {`${getFormatDate(
                    location?.upcoming_start_date,
                    "MM/DD/YYYY"
                  )} - ${getFormatDate(
                    location?.upcoming_end_date,
                    "MM/DD/YYYY"
                  )}`}
                </Typography>
              </Typography>
            </div>
            <div className="col-md-1 d-none d-md-block">
              <Divider orientation="vertical" />
            </div>
            <div className="col-md-5">
              <Typography
                className={`subscription-detail ${
                  hasUpcomingSubscription ? "package" : ""
                }`}
                variant="body2"
              >
                Upcoming Plan:{" "}
                <Typography
                  component="span"
                  className="subscription-detail fw-bb d-block"
                  color="text.primary"
                >
                  {location?.upcoming_pacakge_title}
                </Typography>
              </Typography>
            </div>
          </div>
        ) : null}
      </>
    );
  };

  return (
    <LocationContainer
      className={`row align-items-center p-2 location ${
        hasUpcomingSubscription ? "upcoming-subscription-renewd" : ""
      }`}
      disabled={hasUpcomingSubscription}
    >
      <div className="col-md-7 col-lg-8 mb-2 mb-md-0">
        <div className="d-flex align-items-start">
          {!hasUpcomingSubscription &&
          selectedBrand &&
          selectedBrand?.status ? (
            <Checkbox
              edge="start"
              inputProps={{
                "aria-label": `Location ${location.fk_location_id} ${location.location_name} (${location.participantName}) checkbox`,
              }}
              color="primary"
              name="selectedLocations"
              value={location.fk_location_id}
              onChange={handleOnChangeLocation}
              onBlur={formProps.handleBlur}
              checked={isChecked}
              className="py-0"
              disabled={hasUpcomingSubscription ? true : false}
            />
          ) : (
            <span style={{ width: 0 }}></span>
          )}
          <div className="min-w-0">
            <Typography
              color={hasUpcomingSubscription ? "text.primary" : "primary"}
              className="ml-1 mb-0 mr-1 location-name d-block"
              component="label"
              variant="body2"
            >{`${location.location_name} (${location.participantName})`}</Typography>
            <div className="mt-2 ml-1 d-flex align-items-center flex-wrap gap-1">
              {renderRenewalLabel()} {pastDueLabel()}
            </div>
          </div>
        </div>
        {renderSubscriptionDate()}
      </div>
      {selectedBrand && selectedBrand?.status ? (
        <div className="col-md-5 col-lg-4">
          <MUICustomAutocompleteSelect
            options={packageOptions}
            selectProps={{
              onChange: (v) => {
                handleOnChangePackage(v);
              },
              value:
                formProps.values.locPackSelections.find(
                  (p) =>
                    String(p.locationId) === String(location.fk_location_id)
                )?.packageId || "",
            }}
            disabled={!isChecked}
            hideLabel
            className="mt-0"
            all
            allText="Select"
            style={{
              border: 0,
              color: !isChecked && "var(--neutral-500)!important",
            }}
            key={String(isChecked)}
          />
        </div>
      ) : null}
    </LocationContainer>
  );
};

export default function UserLocationList({
  locations = [],
  formProps,
  loadMoreLocations,
  totalLocationCount,
}: UserLocationListProps) {
  const { showConfirmDialog, MUIConfirmDialogBox } = useConfirmDialog();
  const packageOptionsMap = useMemo(() => {
    return locations.reduce<PackageOptionsMap>((acc, location) => {
      acc[String(location.fk_location_id)] =
        location?.packageData?.map((p) => ({
          label: `${p.title}- ${p.period} Months`,
          value: p.id,
        })) || [];
      return acc;
    }, {});
  }, [locations]);

  const isLocationBelongsToSameParticipant = useCallback(
    (locId?: string | number, locPackSelections?: LocPackSelection[]) => {
      if (!locPackSelections?.length) return true;

      const selectedLocationParticipants = locPackSelections.map((l) =>
        String(l?.locationInfo?.fk_participant_id)
      );

      const selectingLocationParticipantID = locations?.find(
        (l) => String(l.fk_location_id) === String(locId)
      )?.fk_participant_id;

      return selectedLocationParticipants?.some(
        (p) => String(p) === String(selectingLocationParticipantID)
      );
    },
    [locations]
  );

  const isLocationBelongsToSameCountry = useCallback(
    (locId?: string | number, locPackSelections?: LocPackSelection[]) => {
      if (!locPackSelections?.length) return true;

      const selectedLocationCountries = locPackSelections.map((l) =>
        String(l?.locationInfo?.country || "")
          .trim()
          .toUpperCase()
      );

      const selectingLocationCountry = String(
        locations?.find((l) => String(l.fk_location_id) === String(locId))
          ?.country || ""
      )
        .trim()
        .toUpperCase();

      return selectedLocationCountries?.some(
        (country) => country === selectingLocationCountry
      );
    },
    [locations]
  );

  const isPackageBelongsToSamePaymentType = useCallback(
    (
      locId?: string | number,
      packageId?: string | number,
      locPackSelections?: LocPackSelection[]
    ) => {
      // If no locations are selected or no package is selected for any location, allow selection or when packageId is not provided
      if (
        !locPackSelections?.length ||
        locPackSelections.every((l) => !l?.packageId) ||
        !packageId
      ) {
        return true;
      }

      // Get the package details for the location/package being selected.
      const selectingLocation = locations?.find(
        (l) => String(l.fk_location_id) === String(locId)
      );
      const selectingPackageDetails =
        selectingLocation?.packageData?.find(
          (p) => String(p.id) === String(packageId)
        ) ||
        locations
          ?.flatMap((l) => l?.packageData || [])
          ?.find((p) => String(p.id) === String(packageId));

      // If package details not found, prevent selection
      if (!selectingPackageDetails) {
        return false;
      }

      // Get all locations that have packages selected
      const locationsWithPackages = locPackSelections.filter(
        (l) => l?.packageId && l?.packageInfo
      );

      // If this is the first package being selected, allow it
      if (locationsWithPackages.length === 0) {
        return true;
      }

      // If we're updating the package for a location that already has one,
      // we need to exclude its current package from the payment type check
      const otherLocationsWithPackages = locationsWithPackages.filter(
        (l) => String(l.locationId) !== String(locId)
      );

      // If no other locations have packages after filtering, allow selection
      if (otherLocationsWithPackages.length === 0) {
        return true;
      }

      // Check if the new package's payment type matches all other selected packages
      const hasDifferentPaymentType = otherLocationsWithPackages.some(
        (l) =>
          String(l.packageInfo?.payment_type) !==
          String(selectingPackageDetails?.payment_type)
      );

      return !hasDifferentPaymentType;
    },
    [locations]
  );

  const isPackageBelongsToSameCurrency = useCallback(
    (
      locId?: string | number,
      packageId?: string | number,
      locPackSelections?: LocPackSelection[]
    ) => {
      if (
        !locPackSelections?.length ||
        locPackSelections.every((l) => !l?.packageId) ||
        !packageId
      ) {
        return true;
      }

      const selectingLocation = locations?.find(
        (l) => String(l.fk_location_id) === String(locId)
      );
      const selectingPackageDetails =
        selectingLocation?.packageData?.find(
          (p) => String(p.id) === String(packageId)
        ) ||
        locations
          ?.flatMap((l) => l?.packageData || [])
          ?.find((p) => String(p.id) === String(packageId));

      if (!selectingPackageDetails) {
        return false;
      }

      const locationsWithPackages = locPackSelections.filter(
        (l) => l?.packageId && l?.packageInfo
      );

      if (locationsWithPackages.length === 0) {
        return true;
      }

      const otherLocationsWithPackages = locationsWithPackages.filter(
        (l) => String(l.locationId) !== String(locId)
      );

      if (otherLocationsWithPackages.length === 0) {
        return true;
      }

      const selectingPackageCurrency = String(
        selectingPackageDetails?.currency || ""
      )
        .trim()
        .toUpperCase();

      const hasDifferentCurrency = otherLocationsWithPackages.some((l) => {
        const selectedPackageCurrency = String(l.packageInfo?.currency || "")
          .trim()
          .toUpperCase();

        return selectedPackageCurrency !== selectingPackageCurrency;
      });

      return !hasDifferentCurrency;
    },
    [locations]
  );

  return (
    <>
      <div className="user-location-list">
        <div className="locations upcoming-renewal-locations px-3">
          {locations.map((location) => (
            <Location
              location={location}
              key={location.fk_location_id}
              formProps={formProps}
              packageOptions={
                packageOptionsMap[String(location.fk_location_id)] || []
              }
              isLocationBelongsToSameParticipant={
                isLocationBelongsToSameParticipant
              }
              isLocationBelongsToSameCountry={isLocationBelongsToSameCountry}
              isPackageBelongsToSamePaymentType={
                isPackageBelongsToSamePaymentType
              }
              isPackageBelongsToSameCurrency={isPackageBelongsToSameCurrency}
              showConfirmDialog={showConfirmDialog}
            />
          ))}
        </div>
        {totalLocationCount ? (
          <Typography className="fs-10 mt-3" gutterBottom>
            Locations {locations?.length} of {totalLocationCount}
          </Typography>
        ) : null}
        {loadMoreLocations() as ReactNode}
      </div>
      <MUIConfirmDialogBox />
    </>
  );
}
